import React, { useRef } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { Popover } from 'react-tiny-popover';

import { useParams } from 'react-router-dom';

import { UserSearchResponse } from 'utils/api/modules/user/user.types';

import API from 'utils/api';

import { PaginatedResponse, SearchRequest } from 'utils/api/types';

import { useSearch } from '../../utils/hooks/search';

import AutocompleteMention from './AutocompleteMention';

import {
  AddCommentContainer,
  ContainerAttachment,
  MentionText,
  StyledTextInput,
  IconsContainer,
  StyledAttachmentIcon,
  StyledSmileIcon,
  StyledSendIcon,
} from './styles';
import { AddCommentActivitiesProps } from './types';
import { Box } from '@mui/material';

const emailRegExp = /@([a-zA-Z0-9+._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+ )/gi;

const AddCommentActivities: React.FunctionComponent<AddCommentActivitiesProps> = ({
  onBlur,
  onEscape,
  placeholder = '',
  sendMessage,
  uploadFile,
  value = '',
  sendTestId,
}): React.ReactElement => {
  const [autocompletionStarts, setAutocompletionStarts] = React.useState<number | undefined>(undefined);
  const [autocompletingSelection, setAutocompletingSelection] = React.useState<number | undefined>(undefined);

  const [isEmojiKeyboardOpened, setIsEmojiKeyboardOpened] = React.useState(false);

  const [search, setSearch] = React.useState('');

  const params = useParams<{ publicFormId: string }>();
  // public form id for entities submission
  const { publicFormId } = params;

  const request = React.useCallback(
    (searchParams?: SearchRequest) => API.user.searchUsers({ ...searchParams, public_uuid: publicFormId }),
    []
  );

  const {
    results: userList,
    searchQuery,
    loading,
  } = useSearch<UserSearchResponse>(request, undefined, { limit: 20 }) as {
    results: UserSearchResponse[] | PaginatedResponse<UserSearchResponse>;
    searchQuery: (phrase: string) => Promise<void>;
    loading: boolean;
  };
  const [fragments, setFragments] = React.useState<React.ReactNode>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef(null);

  // It may be an array or an object with a results/count properties
  const results = Array.isArray(userList) ? userList : userList?.results || [];
  const users =
    results?.filter(
      (f: UserSearchResponse) => `${f.name}${f.email}`.toLowerCase().indexOf(search.toLowerCase()) !== -1
    ) || [];

  React.useEffect(() => {
    if (autocompletingSelection !== undefined && autocompletingSelection >= users.length) {
      setAutocompletingSelection(Math.max(0, users.length - 1));
    }
  }, [users, autocompletingSelection]);

  const [message, setMessage] = React.useState(value);
  const [taggedUsers, setTaggedUsers] = React.useState<UserSearchResponse[]>([]);
  const [sendingMessage, setSendingMessage] = React.useState(false);

  const performMessageSending = async (): Promise<void> => {
    if (message === '') {
      return;
    }

    if (sendingMessage) {
      return;
    }

    setSendingMessage(true);
    try {
      await sendMessage(message, taggedUsers);
      updateMessage('');
    } finally {
      setSendingMessage(false);
    }
  };

  const onArrowDown = (event: React.KeyboardEvent): void => {
    if (autocompletingSelection !== undefined) {
      event.preventDefault();

      if (autocompletingSelection < users.length - 1) {
        setAutocompletingSelection((prev) => (prev !== undefined ? prev + 1 : prev));
      }
    }
  };

  const onArrowUp = (event: React.KeyboardEvent): void => {
    if (autocompletingSelection !== undefined) {
      event.preventDefault();

      if (autocompletingSelection > 0) {
        setAutocompletingSelection((prev) => (prev !== undefined ? prev - 1 : prev));
      }
    }
  };

  const closeAutocompletion = (): void => {
    setAutocompletionStarts(undefined);
    setAutocompletingSelection(undefined);
  };

  const openAutocompletion = (event: React.KeyboardEvent, caret: number): void => {
    // @ts-ignore
    setAutocompletionStarts(caret);
    setAutocompletingSelection(0);
  };

  const onChange = (newText: string, caret: number | null): void => {
    if (autocompletionStarts !== undefined) {
      if (newText.slice(autocompletionStarts, autocompletionStarts + 1) !== '@') {
        closeAutocompletion();
      }
      // @ts-ignore
      const searchText = newText.slice(autocompletionStarts + 1, caret as number);
      if ([' ', '\n'].indexOf(searchText[search.length - 1]) !== -1) {
        closeAutocompletion();
      } else {
        setSearch(searchText);
        searchQuery(searchText);
      }
    }

    const resultText = newText.replace(/^\n+/g, '');

    if (resultText !== message) {
      updateMessage(resultText);
    } else {
      updateMessage(resultText.slice(1));
    }
  };

  const updateMessage = React.useCallback(
    (newMessage: string): void => {
      let remainingText = newMessage;

      const newFragments = [];
      const newTaggedUsers = [];
      const mentionedUsers = remainingText.match(emailRegExp) || [];
      for (const user of mentionedUsers) {
        const starts = remainingText.indexOf(user);
        newFragments.push(remainingText.slice(0, starts));

        const userData =
          results.find((userItem) => user.trim() === `@${userItem.email}`) ||
          taggedUsers.find((userItem) => user.trim() === `@${userItem.email}`);

        if (userData) {
          newFragments.push(
            <MentionText contentEditable={false} data-value={`@${userData.email} `}>
              {`@${userData.name}`}
            </MentionText>
          );
          newTaggedUsers.push(userData);
        } else {
          newFragments.push(user);
        }
        remainingText = remainingText.slice(starts + user.length);
      }
      if (remainingText.length > 0) {
        newFragments.push(remainingText);
      }
      setFragments(newFragments);
      setTaggedUsers(newTaggedUsers);
      setMessage(newMessage);
    },
    [userList, taggedUsers]
  );

  const selectedUser = (position: number, finishes: number): void => {
    if (autocompletionStarts !== undefined && users[position] !== undefined) {
      const newMessage = `${message.slice(0, autocompletionStarts)}@${users[position].email} ${message.slice(
        finishes
      )}`;
      updateMessage(newMessage);
      closeAutocompletion();
    }
  };

  const onEnter = async (_: React.KeyboardEvent<Element>, caret: number): Promise<void> => {
    if (autocompletingSelection === undefined || autocompletionStarts === undefined) {
      await performMessageSending();
    } else {
      selectedUser(autocompletingSelection, caret);
    }
  };

  const onSelectedUser = (selected: number): void => {
    let currentSearchEnds = message.slice(autocompletionStarts).search(/ |\n/);
    currentSearchEnds = currentSearchEnds === -1 ? message.length : currentSearchEnds + (autocompletionStarts || 0);
    // @ts-ignore
    selectedUser(selected, currentSearchEnds);
  };

  const uploadFileList = async (fileList: FileList | null): Promise<void> => {
    const uploadPromises = [];
    for (let i = 0; fileList !== null && i < fileList.length; i += 1) {
      if (uploadFile !== undefined) {
        uploadPromises.push(uploadFile(fileList[i] as File));
      }
    }
    await Promise.all(uploadPromises);
  };

  const onFileUpload = async ({ target }: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    await uploadFileList(target.files);
    target.value = '';
  };

  const onDropFiles = async (event: React.DragEvent<HTMLInputElement>): Promise<void> => {
    event.preventDefault();
    await uploadFileList(event.dataTransfer.files);
  };

  return (
    <>
      <Popover
        isOpen={isEmojiKeyboardOpened}
        positions={['top', 'left']}
        align="end"
        onClickOutside={() => {
          setIsEmojiKeyboardOpened(false);
          if (inputRef.current) {
            inputRef.current.blur();
          }
        }}
        containerStyle={{ zIndex: '1500' }}
        content={
          <Picker
            data={data}
            onEmojiSelect={({ native }: { native: string }) => {
              const strippedMessage = message.endsWith('\n') ? message.substring(0, message.length - 1) : message;
              onChange(`${strippedMessage}${native}`, null);
              if (inputRef.current) {
                inputRef.current.blur();
              }
            }}
            theme="light"
            autoFocus
          />
        }
      >
        <Box sx={{ position: 'relative' }}>
          {autocompletingSelection !== undefined && (
            <AutocompleteMention
              onSelectedUser={onSelectedUser}
              users={users}
              selected={autocompletingSelection}
              loading={loading}
            />
          )}
          {uploadFile && <input hidden multiple onChange={onFileUpload} type="file" ref={fileRef} />}
          <AddCommentContainer>
            <IconsContainer>
              <StyledSmileIcon
                onClick={() => {
                  setIsEmojiKeyboardOpened((prevState) => !prevState);
                }}
              />
              {uploadFile && (
                <StyledAttachmentIcon onClick={() => (fileRef.current || { click: () => undefined }).click()} />
              )}
              <StyledSendIcon
                data-testid={sendTestId}
                disabled={sendingMessage || message.length === 0}
                onClick={() => {
                  if (sendingMessage) {
                    return;
                  }
                  if (message.length > 0) {
                    performMessageSending();
                  }
                }}
              />
            </IconsContainer>
            <ContainerAttachment>
              <StyledTextInput
                componentRef={inputRef}
                disabled={sendingMessage}
                multiLine
                onChange={onChange}
                onClick={() => {
                  closeAutocompletion();
                  setIsEmojiKeyboardOpened(false);
                }}
                onBlur={onBlur || closeAutocompletion}
                onDrop={onDropFiles}
                onEnter={onEnter}
                onEscape={onEscape || closeAutocompletion}
                onKey={{
                  ArrowUp: onArrowUp,
                  ArrowDown: onArrowDown,
                  '@': openAutocompletion,
                }}
                placeholder={placeholder}
                value={fragments}
              />
            </ContainerAttachment>
          </AddCommentContainer>
        </Box>
      </Popover>
    </>
  );
};

export default AddCommentActivities;
